import * as XLSX from "xlsx";
import fileDownload from "js-file-download";
import { exportFilename } from "./chartExport";

// Every report download is an XLSX file. Columns open wide enough to read
// every header in full, and the content up to a cap, so no one has to widen a
// column to see what it is. (The organization service sizes the files it
// builds - the MDA template - the same way: reporting/export.ts columnWidth.)

const XLSX_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
const MAX_CONTENT_WIDTH = 60;

// characters a value takes in a cell: its longest line, a date as YYYY-MM-DD
const textLength = (value) => {
  if (value == null) return 0;
  if (value instanceof Date) return 10;
  return Math.max(...String(value).split("\n").map((line) => line.length));
};

/**
 * Column widths, in characters, for rows whose first row is the header:
 * always the whole header (with room for wide letters), and the content up
 * to a cap.
 */
export const columnWidths = (rows) => {
  const [header = [], ...body] = rows;
  const count = body.reduce((n, row) => Math.max(n, row.length), header.length);
  return Array.from({ length: count }, (_, c) => {
    const headerWidth = Math.ceil(textLength(header[c]) * 1.15) + 2;
    const contentWidth = body.reduce((w, row) => Math.max(w, textLength(row[c]) + 2), 0);
    return { wch: Math.max(headerWidth, Math.min(contentWidth, MAX_CONTENT_WIDTH), 8) };
  });
};

/** A sheet from rows of cells (the first row the header), its columns sized. */
export const sheetFromRows = (rows, options) => {
  const sheet = XLSX.utils.aoa_to_sheet(rows, options);
  sheet["!cols"] = columnWidths(rows);
  return sheet;
};

/** Save one sheet as "<title>-YYYY-MM-DD.xlsx". */
export const downloadSheet = (sheet, sheetName, title) => {
  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, sheet, sheetName.slice(0, 31));
  const buffer = XLSX.write(book, { bookType: "xlsx", type: "array" });
  fileDownload(new Blob([buffer], { type: XLSX_TYPE }), exportFilename(title, "xlsx"));
};

/** Rows of cells (the first row the header) as an XLSX download. */
export const downloadXlsx = ({ title, rows, sheetName = "Data" }) =>
  downloadSheet(sheetFromRows(rows), sheetName, title);
